<template>
  <div class="order-item-card">
    <!-- Media Section -->
    <div v-if="hasMedia" class="order-item-media">
      <!-- View Toggle -->
      <div v-if="hasTabs" class="media-view-toggle mb-2">
        <label class="toggle-label" @click="toggleView">
          <span class="toggle-checkbox" :class="{ 'is-checked': activeTab === 'checkin' }">
            <CIcon v-if="activeTab === 'checkin'" name="cil-check" size="sm"/>
          </span>
          <span class="toggle-text text-muted">
            <translate translate-context="accounting.orderItem">Show all checkin photos</translate>
          </span>
        </label>
      </div>

      <!-- Media Grid -->
      <MediaGrid
        :items="currentMedia.items"
        :loading="currentMedia.loading"
        :pagination="currentMedia.pagination"
        :selected="selectedMediaIds"
        :context-actions="contextActions"
        :collapsible="true"
        :collapsed-rows="2"
        :infinite-scroll="true"
        @update:selected="selectedMediaIds = $event"
        @load-more="loadMore"
        @action="onAction"
      >
        <template #selection-actions="{ selected, clear }">
          <CButton
            v-if="canDownload"
            color="light"
            size="sm"
            @click="downloadSelected(selected)"
          >
            <CIcon name="cil-cloud-download" size="sm" class="mr-1"/>
            <translate translate-context="accounting.orderItem">Download</translate>
          </CButton>
          <CButton
            v-if="canManagePhotos"
            color="danger"
            size="sm"
            @click="removeSelected"
          >
            <CIcon name="cil-trash" size="sm" class="mr-1"/>
            <translate translate-context="accounting.orderItem">Remove</translate>
          </CButton>
          <CButton
            v-if="canManagePhotos && hasSelectedWithStatus"
            color="warning"
            size="sm"
            @click="clearStatusSelected"
          >
            <CIcon name="cil-x-circle" size="sm" class="mr-1"/>
            <translate translate-context="accounting.orderItem">Clear status</translate>
          </CButton>
          <CButton color="secondary" size="sm" variant="ghost" @click="clear">
            <translate translate-context="accounting.orderItem">Clear</translate>
          </CButton>
        </template>

        <!-- Photo status overlay -->
        <template #item-overlay="{ item }">
          <!-- Purchased indicator (bottom-left) -->
          <div
            v-if="item.is_purchased"
            class="photo-status-badge photo-status-purchased"
            :title="$pgettext('accounting.orderItem', 'Purchased')"
          >
            <CIcon name="cil-lock-unlocked" size="sm"/>
          </div>
          <!-- Remove status (top-right) -->
          <div
            v-if="isRemoved(item.id)"
            class="photo-status-badge photo-status-remove"
            :title="$pgettext('accounting.orderItem', 'Marked for removal')"
          >
            <CIcon name="cil-trash" size="sm"/>
          </div>
        </template>
      </MediaGrid>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MediaGrid from '@/domain/core/components/MediaGrid.vue'

export default {
  name: 'OrderItemCard',
  components: {
    MediaGrid
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    canDownload: {
      type: Boolean,
      default: false
    },
    canManagePhotos: {
      type: Boolean,
      default: false
    },
    removedPhotoIds: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      activeTab: 'order',
      selectedMediaIds: []
    }
  },

  computed: {
    ...mapGetters('accounting/order', ['getOrderItemMedia', 'getCheckinMedia']),

    orderMedia() {
      const data = this.getOrderItemMedia(this.item.id)
      return {
        items: data?.media || [],
        loading: data?.loading || false,
        pagination: data?.pagination || null
      }
    },

    checkinMedia() {
      const data = this.getCheckinMedia(this.item.id)
      return {
        items: data?.media || [],
        loading: data?.loading || false,
        pagination: data?.pagination || null
      }
    },

    currentMedia() {
      return this.activeTab === 'order' ? this.orderMedia : this.checkinMedia
    },

    hasMedia() {
      return (this.item.media_ids && this.item.media_ids.length > 0) ||
             (this.item.medias_count > 0) ||
             this.orderMedia.items.length > 0
    },

    hasTabs() {
      return !!this.item.picacode
    },

    orderMediaCount() {
      return this.item.medias_count || this.orderMedia.items.length
    },

    checkinMediaCount() {
      return this.checkinMedia.items.length
    },

    hasSelectedWithStatus() {
      return this.selectedMediaIds.some(id =>
        this.removedPhotoIds.includes(id)
      )
    },

    contextActions() {
      const actions = []

      if (this.canDownload) {
        actions.push({
          value: 'download',
          label: this.$pgettext('accounting.orderItem', 'Download'),
          icon: 'cil-cloud-download'
        })
      }

      if (this.canManagePhotos) {
        actions.push({
          value: 'remove',
          label: this.$pgettext('accounting.orderItem', 'Remove photos'),
          icon: 'cil-trash'
        })
        actions.push({
          value: 'clear-status',
          label: this.$pgettext('accounting.orderItem', 'Clear status'),
          icon: 'cil-x-circle'
        })
      }

      return actions
    }
  },

  mounted() {
    this.fetchOrderItemMedia({ orderItemId: this.item.id })
  },

  methods: {
    ...mapActions('accounting/order', [
      'fetchOrderItemMedia',
      'fetchMoreOrderItemMedia',
      'fetchCheckinMedia',
      'fetchMoreCheckinMedia'
    ]),

    toggleView() {
      const newTab = this.activeTab === 'order' ? 'checkin' : 'order'
      this.activeTab = newTab

      if (newTab === 'checkin' && this.checkinMedia.items.length === 0) {
        this.fetchCheckinMedia({ orderItemId: this.item.id })
      }
    },

    loadMore() {
      if (this.activeTab === 'order') {
        this.fetchMoreOrderItemMedia({ orderItemId: this.item.id })
      } else {
        this.fetchMoreCheckinMedia({ orderItemId: this.item.id })
      }
    },

    onAction({ action, items }) {
      if (action === 'download') {
        this.downloadSelected(items.map(i => i.id))
      } else if (action === 'remove') {
        this.$emit('remove-photos', items)
        this.selectedMediaIds = []
      } else if (action === 'clear-status') {
        const photoIds = items.map(i => i.id).filter(id =>
          this.removedPhotoIds.includes(id)
        )
        if (photoIds.length > 0) {
          this.$emit('clear-status-photos', photoIds)
        }
        this.selectedMediaIds = []
      }
    },

    isRemoved(photoId) {
      return this.removedPhotoIds.includes(photoId)
    },

    downloadSelected(mediaIds) {
      this.$emit('download', {
        mediaIds: Array.isArray(mediaIds) ? mediaIds : this.selectedMediaIds,
        orderItemId: this.item.id,
        picaServiceId: this.item.pica_service_id
      })
    },

    removeSelected() {
      const items = this.currentMedia.items.filter(i => this.selectedMediaIds.includes(i.id))
      this.$emit('remove-photos', items)
      this.selectedMediaIds = []
    },

    clearStatusSelected() {
      const photoIds = this.selectedMediaIds.filter(id =>
        this.removedPhotoIds.includes(id)
      )
      this.$emit('clear-status-photos', photoIds)
      this.selectedMediaIds = []
    }
  }
}
</script>

<style scoped>
.order-item-media {
  background: #f8f9fa;
  padding: 1rem;
}

.media-view-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.toggle-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  background: white;
  margin-right: 0.5rem;
  transition: all 0.15s ease;
}

.toggle-checkbox.is-checked {
  background: #321fdb;
  border-color: #321fdb;
  color: white;
}

.toggle-checkbox:hover {
  border-color: #321fdb;
}

.toggle-text {
  font-size: 0.875rem;
}

/* Photo status badges */
.photo-status-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.photo-status-remove {
  background: #ef4444;
  color: white;
}

.photo-status-purchased {
  top: auto;
  bottom: 8px;
  right: auto;
  left: 8px;
  background: #f59e0b;
  color: white;
}
</style>
